import { z } from 'zod';
const subFieldSchema = z.object({
    name: z.string().describe('camelCase sub-field name'),
    description: z.string().optional().describe('Brief description'),
    type: z
        .enum([
        'text',
        'textarea',
        'number',
        'email',
        'phone',
        'date',
        'datetime',
        'address',
        'boolean',
        'options'
    ])
        .describe('Sub-field type'),
    enum: z.array(z.string()).optional().describe('Options for "options" type sub-fields'),
    required: z.boolean().optional()
});
export const aiGenerationFieldConditionSchema = z.union([
    z.object({
        fieldName: z
            .string()
            .describe('The camelCase name of an EARLIER field IN THE SAME BLOCK whose value gates this field. Must be a boolean or options field.'),
        operation: z.enum(['equals', 'not_equals']).describe('Comparison operator.'),
        expectedValue: z
            .union([z.string(), z.boolean()])
            .describe("Value that triggers this field being asked. For boolean gates: true/false. For options gates: one of the gate's enum values.")
    }),
    z.object({
        fieldName: z
            .string()
            .describe('The camelCase name of an EARLIER field IN THE SAME BLOCK whose value gates this field. Must be an options field for inlist/not_inlist.'),
        operation: z
            .enum(['inlist', 'not_inlist'])
            .describe('List membership: inlist matches any of expectedValues, not_inlist matches none.'),
        expectedValues: z
            .array(z.string())
            .min(1)
            .describe("Non-empty list of values from the gate field's enum.")
    })
]);
export const aiGenerationFieldSchema = z.object({
    name: z.string().describe('camelCase field name'),
    sourceKey: z
        .string()
        .optional()
        .describe('The original source field identifier from the reference material (e.g., "CALLER_INFORMATION.CALLER_NAME_FIRST"). Set this when the source JSON provides a machine name for the field.'),
    description: z
        .string()
        .optional()
        .describe('Describes what the data is — NOT what to say. Never start with "Ask:". Use "Whether...", "Who...", etc. Include validation hints and formatting rules'),
    type: z
        .enum([
        'text',
        'textarea',
        'number',
        'email',
        'phone',
        'date',
        'datetime',
        'address',
        'boolean',
        'options',
        'list',
        'object'
    ])
        .describe('Field type'),
    enum: z.array(z.string()).optional().describe('Options for "options" type fields'),
    subFields: z
        .array(subFieldSchema)
        .optional()
        .describe('For "list" type: defines the fields of each item in the collection (e.g., witness has firstName, lastName, phone). For "object" type: defines the nested properties.'),
    required: z.boolean().optional().describe('Whether this field is required'),
    condition: aiGenerationFieldConditionSchema
        .optional()
        .describe('OPTIONAL: gate this single field on an earlier field IN THE SAME BLOCK. Use ONLY for tight single-field follow-ups within the same block (1-2 fields off one answer). For multi-field branches, split into a new block with a block-level condition instead.')
});
export const aiGenerationConditionSchema = z.union([
    z.object({
        fieldName: z
            .string()
            .describe('The camelCase name of a field from a PREVIOUS block whose value determines whether this block is shown. e.g. "isOwner", "authoritiesInvolved", "claimantType"'),
        operation: z
            .enum(['equals', 'not_equals'])
            .describe('Whether the field must equal or not equal the expected value'),
        expectedValue: z
            .union([z.string(), z.boolean()])
            .describe('The value that triggers this block. e.g. true, false, "Business", "Theft"')
    }),
    z.object({
        fieldName: z
            .string()
            .describe('The camelCase name of an options field from a PREVIOUS block whose value determines whether this block is shown.'),
        operation: z
            .enum(['inlist', 'not_inlist'])
            .describe('List membership: inlist matches any of expectedValues, not_inlist matches none.'),
        expectedValues: z
            .array(z.string())
            .min(1)
            .describe("Non-empty list of values from the gate field's enum. e.g. for `lineOfBusiness in {auto, home}`: ['auto','home'].")
    })
]);
export const aiGenerationBlockSchema = z.object({
    title: z.string().describe('camelCase block title, e.g. "callerInformation", "vehicleDetails"'),
    instructions: z
        .string()
        .describe('Sequential, conversational instructions using "Confirm or ask" as the verb for each field. Describes how to collect this block\'s information one question at a time during a live call'),
    icon: z
        .string()
        .describe('Lucide icon name for the block, e.g. "user", "car", "shield", "home", "phone", "file-text", "alert-circle"'),
    condition: aiGenerationConditionSchema.optional(),
    fields: z
        .array(aiGenerationFieldSchema)
        .describe('Fields in this block (maximum 8 — split into multiple blocks if more are needed)')
});
export const aiGenerationFormSchema = z.object({
    blocks: z.array(aiGenerationBlockSchema).describe('The intake form blocks'),
    explanation: z.string().describe('Brief explanation of the generated form structure')
});
//# sourceMappingURL=ai-generation-schema.js.map